import { Flex, GapSize } from "@/components/atoms/flex";
import { useIntl } from "react-intl";
import { useStyle } from "./style";
import {
	Button,
	Field,
	Textarea,
	Divider,
	Spinner,
} from "@fluentui/react-components";
import { useCallback, useMemo, useState, useRef } from "react";
import { InteractionNotesProps } from "./types";
import { createInteractionNote } from "@/repository/queryFns/history";
import { createContactNote } from "@/repository/queryFns/contacts";
import { Validations } from "@/common/constants";
import { Note, NoteType } from "@/components/organisms/history/types";
import { Lockr } from "@/common/Lockr";
import { StorageKeys } from "@/common/constants/storageKeys";
import TooltipWithMount from "@/components/atoms/tooltipWithMount/TooltipWithMount";
import { PanelHeader } from "@/components/atoms/panelHeader";
import { Close32Regular } from "@/common/icons/closeIcon/Close32Regular";
import {
	CurrentInteractionState,
	useCurrentInteractionStore,
} from "@/common/stores/nonpersistingStores/currentInteraction";
import { InteractionType } from "@/common/types/interactionType";
import {
	getCallSummary,
	getEmailSummary,
	getWebchatSummary,
	getWhatsAppSummary,
} from "@/repository/queryFns";
import {
	DocumentOnePageSparkle24Regular,
	Notepad20Regular,
	NotepadPerson20Regular,
} from "@fluentui/react-icons";
import { InteractionNoteItem } from "./interactionNoteItem";
import { getSummaryTooltip } from "@/common/helpers/summaryHelper";
import { useIsAicLockedForInteraction } from "@/hooks/aicLocking";
import { useIsSummaryGenerating } from "@/common/stores/nonpersistingStores/summaryGeneration/summaryGenerationStore";

export const InteractionNotesPopover = (props: InteractionNotesProps) => {
	const intl = useIntl();
	const classes = useStyle();

	const noteText = useRef<string>("");
	const [summary, setSummary] = useState<string>("");
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [isGenerated, setIsGenerated] = useState<boolean>(false);
	const disableSaveButton = useRef<boolean>(false);
	const [noteTextareaValid, setNoteTextareaValid] = useState<
		"error" | "warning" | "success" | "none"
	>("none");
	const [inputValidationMessage, setInputValidationMessage] = useState("");
	const onInputChange = (ev: any) => {
		noteText.current = ev.target.value;
		setSummary(ev.target.value);
	};

	const createNote = useCallback(() => {  
		if (disableSaveButton.current) {
			return;
		} else {
			disableSaveButton.current = true;
			setTimeout(() => {
				disableSaveButton.current = false;
			}, 1000);
		}

		if (!noteText.current) {
			setNoteTextareaValid("error");
			setInputValidationMessage(intl.formatMessage({ id: "Notes.Required" }));

			return;
		}

		if (isNoteFormatValid(noteText.current)) {
			const interactionId = props.callId ? props.callId : props.interactionId;
			const agentId = Lockr.get(StorageKeys.UserId);

			let newNote = {
				interactionId: interactionId,
				contactIdentifier: props.contactId,
				note: noteText.current.trim(),
				agentId: agentId,
				type: props.type,
				agentName: props.agentName,
				isWrapUp: props.isWrapUp,
			} as Note;

			if (props.type === NoteType.InteractionNote) {
				createInteractionNote(newNote).then((_) => {
					props.closePopover();
					props.afterCreatingNote(newNote);
				});
			} else {
				createContactNote(newNote).then((_) => {
					props.closePopover();
					props.afterCreatingNote(newNote);
				});
			}
		}
	}, [noteText]);

	const maxNoteLength = useMemo(
		() =>
			props.type === NoteType.ContactNote
				? Validations.ContactNoteMaxLength
				: Validations.InteractionNoteMaxLength,
		[props.type]
	);

	const isNoteFormatValid = useCallback(
		(text: string) => {
			if (text.length > maxNoteLength) {
				setNoteTextareaValid("error");
				setInputValidationMessage(
					intl.formatMessage(
						{ id: "Common.Validations.MaxLength" },
						{ maxLength: maxNoteLength }
					)
				);
				return false;
			}

			return true;
		},
		[noteText, maxNoteLength]
	);

	const validateInputMaxLength = (event: any) => {
		const value = event.target.value;
		if (value && !isNoteFormatValid(value)) {
			return;
		}

		setNoteTextareaValid("none");
		setInputValidationMessage("");
	};

	const currentInteraction = useCurrentInteractionStore(
		(state: CurrentInteractionState) => state.active
	);

	const isAicLocked = useIsAicLockedForInteraction(currentInteraction?.firstAgentAssignedAt);

	// True while the close-time summary is generated in the background (set during wrap-up).
	const isSummaryGenerating = useIsSummaryGenerating(
		currentInteraction?.sessionId ?? currentInteraction?.id
	);

	const getSummaryFn = useMemo(() => {
		switch (currentInteraction?.type) {
			case InteractionType.Email:
				return getEmailSummary;
			case InteractionType.WhatsApp:
				return getWhatsAppSummary;
			case InteractionType.Webchat:
				return getWebchatSummary;
			case InteractionType.Call:
				return getCallSummary;
			default:
				return () => Promise.resolve("");
		}
	}, [currentInteraction?.type]);

	const getSummary = useCallback(async () => {
		if (!currentInteraction?.id) {
			return;
		}

		setIsLoading(true);

		let aiSummary = await getSummaryFn(
			currentInteraction.sessionId ?? currentInteraction.id,
			currentInteraction.startDate
		);

		if (typeof aiSummary !== "string") {
			// retry
			aiSummary = await getSummaryFn(
				currentInteraction.sessionId ?? currentInteraction.id,
				currentInteraction.startDate
			);
		}

		if (typeof aiSummary === "string" && aiSummary.trim().length > 0) {
			const cleanSummary = aiSummary.replace(/<[^>]*>?/gm, ""); // remove html tags
			noteText.current = cleanSummary;
			setSummary(cleanSummary);
			setIsGenerated(true);
		}

		setIsLoading(false);
	}, [currentInteraction, getSummaryFn]);

	const isCallInteraction = currentInteraction?.type === InteractionType.Call && currentInteraction?.isUnify;
	const isTextInteraction = currentInteraction?.type === InteractionType.Email ||
		currentInteraction?.type === InteractionType.WhatsApp ||
		currentInteraction?.type === InteractionType.Webchat;

	const isSummaryFeatureUnavailable = isCallInteraction
		? !(props.enableCallSummary ?? false)
		: !(props.enableTextSummary ?? false);

	const autoInsert = isCallInteraction ? props.autoInsertCallSummary : props.autoInsertTextSummary;

	const showGenerateSummaryButton =
		!isGenerated &&
		props.isWrapUp &&
		!autoInsert &&
		(isCallInteraction || isTextInteraction) ? (
			<TooltipWithMount
				content={
					isSummaryFeatureUnavailable || !props.canGenerateSummary
						? getSummaryTooltip(
								intl,
								isSummaryFeatureUnavailable,
								props.canGenerateSummary
							)
						: isAicLocked
							? intl.formatMessage({ id: "Interaction.Buttons.FeatureNotAvailable" })
							: getSummaryTooltip(
									intl,
									isSummaryFeatureUnavailable,
									props.canGenerateSummary
								)
				}
				relationship="label"
			>
				<Button
					icon={<DocumentOnePageSparkle24Regular />}
					onClick={getSummary}
					disabled={
						isSummaryFeatureUnavailable || !props.canGenerateSummary || isAicLocked
					}
				></Button>
			</TooltipWithMount>
		) : (
			<span></span>
		);

	const newNoteEditor = useMemo(
		() => (
			<Flex column>
				<Flex hAlign="flex-start" className={classes.commentInput}>
					{intl.formatMessage({
						id: `Notes.AddComments`,
					})}
				</Flex>
				<Field
					validationState={noteTextareaValid}
					validationMessage={inputValidationMessage}
					className={classes.noteField}
				>
					<Textarea
						className={classes.noteInput}
						appearance="filled-lighter"
						onChange={onInputChange}
						onBlur={validateInputMaxLength}
						value={summary}
						style={{ height: summary.length ? "110px" : "37px" }}
					/>
				</Field>
				<Flex hAlign="space-between">
					{isLoading || isSummaryGenerating ? (
						<Spinner
							size="tiny"
							label={intl.formatMessage({
								id: isSummaryGenerating ? "Summary.Generating" : "Common.Loading",
							})}
						/>
					) : (
						showGenerateSummaryButton
					)}
					<Flex gap={GapSize.Small} hAlign="flex-end">
						<Button onClick={props.closePopover} className={classes.button}>
							{intl.formatMessage({ id: "Common.Cancel" })}
						</Button>
						<Button
							appearance="primary"
							className={classes.button}
							onClick={createNote}
						>
							{intl.formatMessage({ id: "Notes.Save" })}
						</Button>
					</Flex>
				</Flex>
				<br />
				<Divider />
			</Flex>
		),
		[
			noteTextareaValid,
			inputValidationMessage,
			summary,
			isLoading,
			isSummaryGenerating,
			currentInteraction,
		]
	);

	return (
		<Flex column className={classes.container}>
			<PanelHeader
				left={
					<Flex hAlign="flex-start" column={false} vAlign="center">
						{props.interactionNotes.length > 0 ? (
							<NotepadPerson20Regular />
						) : (
							<Notepad20Regular />
						)}
						<p className={classes.title}>
							{intl.formatMessage({
								id: `Notes.${props.type === NoteType.InteractionNote ? "InteractionNotes" : "ContactNotes"}`,
							})}
						</p>
					</Flex>
				}
				right={
					<Button
						appearance="transparent"
						shape="circular"
						icon={<Close32Regular />}
						size="small"
						onClick={props.closePopover}
					/>
				}
				className={classes.panelHeader}
			/>
			<Divider />
			{props.editMode && (
				<>
					{newNoteEditor}
					<Flex className={classes.subTitle}>
						{intl.formatMessage({ id: "Notes.PreviousComments" })}
					</Flex>
				</>
			)}
			<Flex hAlign="flex-start" column className={classes.notesContainer}>
				{props.interactionNotes.map((note, index) => {
					const isLast = index === props.interactionNotes.length - 1;

					return <InteractionNoteItem note={note} isLast={isLast} />;
				})}
			</Flex>
		</Flex>
	);
};
